import { DatePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { RouterLink } from '@angular/router';
import {
  ClientSideDataTable,
  Column,
  DialogService,
  OrganizationService,
  Store,
  StoreService,
  TitleService,
  User,
  UserFormComponent,
  UserService
} from '@ng-cloud/badger-core';
import { AuthorizationService } from '@ng-cloud/badger-core/services/authorization.service';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { FlexDirective, LayoutAlignDirective, LayoutDirective, LayoutGapDirective } from '@ngbracket/ngx-layout/flex';

import * as _ from 'lodash';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'bt-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
  imports: [
    DatePipe,
    FlexDirective,
    LayoutAlignDirective,
    LayoutDirective,
    LayoutGapDirective,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFormField,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatPaginator,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatSuffix,
    MatTable,
    ReactiveFormsModule,
    RouterLink,
    TitleCasePipe
  ]
})
export class UsersComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  dataTable: ClientSideDataTable<User>;

  roleOptions: string[];
  stores: Store[] = [];
  currentUser: User;

  constructor(
    public dialog: MatDialog,
    private userService: UserService,
    private storeService: StoreService,
    private organizationService: OrganizationService,
    private dialogService: DialogService,
    public titleService: TitleService,
    public media: MediaObserver,
    private authService: AuthorizationService
  ) {
  }

  ngOnInit() {
    const currentUserId = this.authService.getCurrentUserId();

    this.titleService.setTitle('Users');

    this.userService.getUser(currentUserId).subscribe((fullUser) => {
      this.currentUser = fullUser;

      this.createUsersTable();

      this.dataTable.sort = this.sort;
      this.dataTable.paginator = this.paginator;
      this.dataTable.mediaObserver = this.media;
      this.dataTable.filter = new UntypedFormGroup({
        filter: new UntypedFormControl()
      });

      this.storeService.getStores().subscribe((stores) => {
        this.stores = stores;
        this.userService
          .getUsers()
          .pipe(
            tap((users) =>
              users.forEach((user) => {
                user.organizationId &&
                this.organizationService.addOrganization(user).subscribe();
                user.homeStoreId &&
                (user.store = _.find(
                  this.stores,
                  (store) => store.id === user.homeStoreId
                ));
              })
            )
          )
          .subscribe((users) => (this.dataTable.data = users));
      });
    });

    this.userService
      .getRoles()
      .subscribe((roles) => (this.roleOptions = roles));
  }

  private createUsersTable() {
    const columnDefs: Column[] = [];

    columnDefs.push(new Column('email'));
    columnDefs.push(new Column('firstName'));
    columnDefs.push(new Column('lastName'));

    if (this.currentUser.roles.includes('admin')) {
      columnDefs.push(new Column('organization.name'));
    }

    columnDefs.push(new Column('store.name'));
    columnDefs.push(new Column('roles', { property: 'rolesString' }));
    columnDefs.push(new Column('lastSignInAt', { mediaQuery: 'gt-xs' }));
    columnDefs.push(new Column('addOrDelete'));

    this.dataTable = new ClientSideDataTable<User>(columnDefs);
  }

  deleteUser(user: User): void {
    this.dialogService
      .confirm(`Are you sure you want to delete "${user.email}"`)
      .subscribe((confirmed) => {
        if (confirmed) {
          this.userService
            .deleteUser(user)
            .subscribe(
              () =>
                (this.dataTable.data = this.dataTable.data.filter(
                  (u) => u !== user
                ))
            );
        }
      });
  }

  openCreate(): void {
    const newUser = new User();
    // Build simulation parameters (if properties are not set, pass null/empty array)
    const simulationParams = {
      organization_id: newUser.organizationId || null,
      roles: newUser.roles || []
    };

    this.userService
      .getPotentialStoreIds(simulationParams)
      .subscribe((simulationResult) => {
        const potentialStoreIds = simulationResult.potential_store_ids; // either an array or 'unrestricted'
        const dialogRef = this.dialog.open(UserFormComponent, {
          data: {
            user: newUser,
            roleOptions: this.roleOptions,
            potentialStoreIds: potentialStoreIds
          }
        });

        dialogRef.afterClosed().subscribe((result: User) => {
          if (result) {
            this.dataTable.data = this.dataTable.data.concat([result]);
          }
        });
      });
  }

  openEdit(user): void {
    this.userService
      .getUser(user.id, { include_allowed_store_data: 'true' })
      .subscribe((fullUserData) => {
        const simulationParams = {
          organization_id: fullUserData.organizationId,
          roles: fullUserData.roles,
          user_id: user.id
        };

        this.userService
          .getPotentialStoreIds(simulationParams)
          .subscribe((simulationResult) => {
            const potentialStoreIds = simulationResult.potential_store_ids;

            const dialogRef = this.dialog.open(UserFormComponent, {
              data: {
                user: fullUserData,
                roleOptions: this.roleOptions,
                potentialStoreIds: potentialStoreIds
              }
            });

            dialogRef.afterClosed().subscribe((result: User) => {
              if (result) {
                Object.assign(
                  this.dataTable.data.find((r) => result.id == r.id),
                  result
                );
              }
            });
          });
      });
  }

  canEditOrDelete(user: User): boolean {
    return this.currentUser.canEditOrDelete(user);
  }
}
